import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";

export default function UsersPage() {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [poojas, setPoojas] = useState([]);
  const [creating, setCreating] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "guruji", assigned_poojas: [] });

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); api.get("/poojas").then(({ data }) => setPoojas(data)); }, []);

  const togglePooja = (slug) => {
    setForm((f) => ({
      ...f,
      assigned_poojas: f.assigned_poojas.includes(slug)
        ? f.assigned_poojas.filter((s) => s !== slug)
        : [...f.assigned_poojas, slug],
    }));
  };

  const create = async () => {
    if (!form.email || !form.password || !form.name) {
      return toast.error("Email, password and name are required");
    }
    try {
      await api.post("/users", form);
      toast.success(`User created: ${form.email}`);
      setCreating(false);
      setForm({ email: "", password: "", name: "", role: "guruji", assigned_poojas: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const resetPassword = async () => {
    const pwd = document.getElementById("reset-pwd").value;
    if (!pwd) return toast.error("Enter a new password");
    try {
      await api.patch(`/users/${resetUser.id}`, { password: pwd });
      toast.success(`Password updated for ${resetUser.email}`);
      setResetUser(null);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const updateAssigned = async (u, slugs) => {
    try {
      await api.patch(`/users/${u.id}`, { assigned_poojas: slugs });
      toast.success("Assignments updated");
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl">{t("admin.users")}</h1>
        <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full btn-brand text-sm" data-testid="users-add-btn">
          <Plus size={14} /> {t("admin.addUser")}
        </button>
      </div>

      <div className="mt-6 bg-white border border-[color:var(--border)] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--bg-2)] text-left">
            <tr>
              <th className="p-3">{t("admin.name")}</th>
              <th className="p-3">{t("admin.email")}</th>
              <th className="p-3">{t("admin.role")}</th>
              <th className="p-3">{t("admin.assignedPoojas")}</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[color:var(--border)]" data-testid={`user-row-${u.id}`}>
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3 opacity-80">{u.email}</td>
                <td className="p-3 capitalize"><span className="text-xs px-2 py-0.5 rounded-full bg-[color:var(--bg-2)]">{u.role}</span></td>
                <td className="p-3">
                  {u.role === "guruji" ? (
                    <PoojaAssignEditor user={u} poojas={poojas} onChange={(slugs) => updateAssigned(u, slugs)} />
                  ) : (
                    <span className="text-xs opacity-60">—</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => setResetUser(u)} className="px-2 py-1 text-xs border border-[color:var(--border)] rounded-md mr-1" data-testid={`reset-pwd-${u.id}`}>
                    <KeyRound size={12} className="inline mr-1" /> {t("admin.resetPassword")}
                  </button>
                  {u.id !== me?.id && (
                    <button onClick={() => removeUser(u)} className="px-2 py-1 text-xs border border-[color:var(--border)] rounded-md text-red-700" data-testid={`del-user-${u.id}`}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <Modal title={t("admin.addUser")} onClose={() => setCreating(false)} testid="create-user-modal">
          <div className="space-y-3">
            <div><Label>{t("admin.name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-name" /></div>
            <div><Label>{t("admin.email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-email" /></div>
            <div><Label>{t("admin.password")}</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="create-password" /></div>
            <div>
              <Label>{t("admin.role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="create-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="guruji">Guruji</SelectItem>
                  <SelectItem value="helper">Helper</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "guruji" && (
              <div>
                <Label>{t("admin.assignedPoojas")}</Label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {poojas.map((p) => (
                    <button
                      key={p.slug}
                      type="button"
                      onClick={() => togglePooja(p.slug)}
                      data-testid={`create-assign-${p.slug}`}
                      className={`px-3 py-1.5 text-xs rounded-full border ${form.assigned_poojas.includes(p.slug) ? "bg-[color:var(--brand)] text-[#faf8f5] border-[color:var(--brand)]" : "bg-white border-[color:var(--border)]"}`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={() => setCreating(false)} className="px-4 py-2 rounded-full text-sm border border-[color:var(--border)]" data-testid="create-cancel">Cancel</button>
            <button onClick={create} className="px-5 py-2 rounded-full btn-brand text-sm" data-testid="create-submit">{t("admin.create")}</button>
          </div>
        </Modal>
      )}

      {resetUser && (
        <Modal title={`${t("admin.resetPassword")} · ${resetUser.email}`} onClose={() => setResetUser(null)} testid="reset-modal">
          <div className="space-y-3">
            <Label>{t("admin.newPassword")}</Label>
            <Input id="reset-pwd" type="text" data-testid="reset-pwd-input" />
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={() => setResetUser(null)} className="px-4 py-2 rounded-full text-sm border border-[color:var(--border)]">Cancel</button>
            <button onClick={resetPassword} className="px-5 py-2 rounded-full btn-brand text-sm" data-testid="reset-submit">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function PoojaAssignEditor({ user, poojas, onChange }) {
  const [open, setOpen] = useState(false);
  const assigned = user.assigned_poojas || [];
  const labels = assigned.map((s) => poojas.find((p) => p.slug === s)?.name).filter(Boolean);
  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} className="text-left text-xs" data-testid={`assigned-toggle-${user.id}`}>
        {labels.length ? labels.join(", ") : <span className="opacity-60">No poojas assigned</span>}
      </button>
      {open && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {poojas.map((p) => {
            const on = assigned.includes(p.slug);
            return (
              <button
                key={p.slug}
                onClick={() => onChange(on ? assigned.filter((s) => s !== p.slug) : [...assigned, p.slug])}
                data-testid={`assign-${user.id}-${p.slug}`}
                className={`px-2 py-1 text-[11px] rounded-full border ${on ? "bg-[color:var(--brand)] text-[#faf8f5] border-[color:var(--brand)]" : "bg-white border-[color:var(--border)]"}`}
              >
                {p.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-lg my-10 p-6" data-testid={testid}>
        <h3 className="font-heading text-2xl mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}
